import { ErrorEnvelopeSchema, type ErrorCode } from "@safeplate/contracts";

export class ApiError extends Error {
  // The response body as sent, for errors that carry more than a message.
  readonly body: unknown;
  readonly code: ErrorCode | "network";
  // The request field the error is about, such as "people.2.email".
  readonly field: string | undefined;
  readonly requestId: string | undefined;
  readonly status: number;

  constructor(input: {
    body?: unknown;
    code: ErrorCode | "network";
    field?: string;
    message: string;
    requestId?: string;
    status: number;
  }) {
    super(input.message);
    this.name = "ApiError";
    this.body = input.body;
    this.code = input.code;
    this.field = input.field;
    this.requestId = input.requestId;
    this.status = input.status;
  }
}

export function apiErrorFromResponse(
  response: Response,
  body: unknown,
): ApiError {
  const envelope = ErrorEnvelopeSchema.safeParse(body);

  if (envelope.success) {
    return new ApiError({
      body,
      code: envelope.data.error.code,
      ...(envelope.data.error.field
        ? { field: envelope.data.error.field }
        : {}),
      message: envelope.data.error.message,
      requestId: envelope.data.error.request_id,
      status: response.status,
    });
  }

  // A newer server can send a code this build doesn't know yet; keep its
  // field, and its message for a 4xx (a 5xx keeps the stable wording).
  const loose = (body as { error?: { field?: unknown; message?: unknown } })
    ?.error;
  const field = typeof loose?.field === "string" ? loose.field : undefined;
  const message =
    response.status < 500 &&
    typeof loose?.message === "string" &&
    loose.message.trim()
      ? loose.message
      : undefined;
  return new ApiError({
    body,
    code: response.status === 401 ? "unauthenticated" : "internal",
    ...(field ? { field } : {}),
    message:
      response.status === 401
        ? "Please sign in to continue."
        : (message ?? "SafePlate could not complete that request."),
    status: response.status,
  });
}

export function networkError(error: unknown): ApiError {
  return new ApiError({
    code: "network",
    message:
      error instanceof Error && error.name === "AbortError"
        ? "The request took too long. Please try again."
        : "SafePlate could not reach the service. Check your connection and try again.",
    status: 0,
  });
}

export function displayError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "job_enqueue_failed") {
      return "We couldn’t start that. Try again shortly.";
    }
    return error.message;
  }

  return "Something went wrong. Please try again.";
}
